import React, { useEffect, useRef, useState, useMemo } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import {
  makeStyles,
  IconButton,
  Typography,
  Paper,
  InputBase,
  CircularProgress,
} from '@material-ui/core'
import {
  Room as RoomIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@material-ui/icons'
import { useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import Script from 'react-load-script'
import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import apiConfigs from 'app/configs/apiConfigs'

const useStyles = makeStyles((theme) => {
  const mobile = theme.breakpoints.down(900)
  const desktop = theme.breakpoints.up(900)
  const absolute50 = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  }

  return {
    map: {
      width: '100%',
      height: '100%',
      transition: 'all 0.3s ease',
      '&.blurred': { filter: 'blur(3px)', opacity: 0.6 },
    },
    mapLoading: absolute50,
    searchContainer: {
      position: 'absolute',
      top: 20,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 3,
      width: '90%',
      maxWidth: 400,
    },
    searchPaper: {
      padding: 8,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      borderRadius: 12,
    },
    searchInput: { marginLeft: theme.spacing(1.5), flex: 1, fontSize: 16 },
    searchIcon: { color: '#757575' },
    autocompleteDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 8,
      backgroundColor: 'white',
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      maxHeight: 250,
      overflowY: 'auto',
      zIndex: 3,
    },
    suggestionItem: {
      padding: '14px 18px',
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0',
      '&:hover': { backgroundColor: '#f5f5f5' },
      '&:last-child': { borderBottom: 'none' },
    },
    centerMarker: {
      ...absolute50,
      transform: 'translate(-50%, -100%)',
      zIndex: 1,
      pointerEvents: 'none',
      animation: '$dropIn 0.3s ease-out',
      '& svg': {
        fontSize: 48,
        color: '#EA4335',
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
      },
    },
    '@keyframes dropIn': {
      from: { transform: 'translate(-50%, -200%)', opacity: 0 },
      to: { transform: 'translate(-50%, -100%)', opacity: 1 },
    },
    noLocationOverlay: {
      ...absolute50,
      zIndex: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      padding: theme.spacing(3),
      borderRadius: theme.spacing(2),
      textAlign: 'center',
      pointerEvents: 'none',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    content: {
      backgroundColor: 'white',
      overflow: 'auto',
      padding: '3rem',
      [mobile]: {
        margin: 'auto',
        borderRadius: '2.4rem',
        width: 'calc(100vw - 50px)',
        height: '100%',
      },
      [desktop]: { width: '50%', borderRadius: '2.4rem 0 0 2.4rem' },
    },
  }
})

const normalizeLocation = (loc) => {
  if (!loc) return null
  const lat = parseFloat(
    loc.latitude || loc.lat || (typeof loc.lat === 'function' && loc.lat()),
  )
  const lng = parseFloat(
    loc.longitude || loc.lng || (typeof loc.lng === 'function' && loc.lng()),
  )
  return lat && lng ? { lat, lng } : null
}

const Map = ({ id, location, onLocationChange, className }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const mapRef = useRef(null)
  const googleMapRef = useRef(null)
  const debounceTimerRef = useRef(null)

  const [mapLoading, setMapLoading] = useState(true)
  const [isLoadingExternalScript, setIsLoadingExternalScript] = useState(true)
  const [searchValue, setSearchValue] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)

  const currentLocation = normalizeLocation(location)

  const googleCurated = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.onboarding.googleCurated({
        placeName: value,
      })
      return response.data
    },
    onSuccess: (data) => {
      setSuggestions(data || [])
      setShowSuggestions(!!(data && data.length))
    },
    onError: (err) => {
      const msgName = err.response?.data?.error?.name
      if (!['PlaceNotFoundError', 'GoogleFailure'].includes(msgName)) {
        dispatch(
          MessageAlert.showError({
            message:
              msgName || formatMessage({ id: 'error.InternalServerError' }),
          }),
        )
      }
      setSuggestions([])
      setShowSuggestions(false)
    },
  })

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        if (value.trim()) {
          googleCurated.mutate(value)
        }
      }, 500),
    [],
  )

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)

    if (!value.trim()) {
      setSuggestions([])
      setShowSuggestions(false)
      debouncedSearch.cancel()
      googleCurated.reset()
    } else {
      debouncedSearch(value)
    }
  }

  const handlePlaceSelect = (item) => {
    setSearchValue(item.displayName || item.address || '')
    setShowSuggestions(false)
    setSuggestions([])

    const newLocation = normalizeLocation({
      lat: item.location?.latitude || item.latitude,
      lng: item.location?.longitude || item.longitude,
    })

    if (googleMapRef.current && newLocation) {
      googleMapRef.current.panTo(newLocation)
      googleMapRef.current.setZoom(15)
    }

    if (onLocationChange && newLocation) {
      onLocationChange({
        location: {
          latitude: newLocation.lat,
          longitude: newLocation.lng,
        },
      })
    }
  }

  const handleClearSearch = () => {
    setSearchValue('')
    setSuggestions([])
    setShowSuggestions(false)
    debouncedSearch.cancel()
    googleCurated.reset()
  }

  // Initialize map
  useEffect(() => {
    let checkInterval

    const initMap = () => {
      if (!window.google?.maps || !mapRef.current || googleMapRef.current)
        return

      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: currentLocation || { lat: 40.7128, lng: -74.006 },
        zoom: currentLocation ? 15 : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
          {
            featureType: 'transit',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }],
          },
        ],
      })

      googleMapRef.current.addListener('center_changed', () => {
        if (!onLocationChange || !currentLocation) return

        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = setTimeout(() => {
          const center = googleMapRef.current.getCenter()
          onLocationChange({
            location: {
              latitude: center.lat(),
              longitude: center.lng(),
            },
          })
        }, 500)
      })

      setMapLoading(false)
    }

    if (window.google?.maps) {
      initMap()
    } else {
      checkInterval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(checkInterval)
          initMap()
        }
      }, 100)
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval)
      clearTimeout(debounceTimerRef.current)
    }
  }, [])

  // Update map when location changes
  useEffect(() => {
    if (!googleMapRef.current || !currentLocation) return

    const center = googleMapRef.current.getCenter()
    if (center) {
      const latDiff = Math.abs(center.lat() - currentLocation.lat)
      const lngDiff = Math.abs(center.lng() - currentLocation.lng)

      if (latDiff > 0.0001 || lngDiff > 0.0001) {
        googleMapRef.current.panTo(currentLocation)
        googleMapRef.current.setZoom(15)
      }
    }
  }, [currentLocation])

  // Cleanup
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
      clearTimeout(debounceTimerRef.current)
    }
  }, [debouncedSearch])

  return (
    <div className={className}>
      <Script
        url={`https://maps.googleapis.com/maps/api/js?key=${apiConfigs.googleAPI}&libraries=places`}
        onCreate={() => setIsLoadingExternalScript(true)}
        onLoad={() => setIsLoadingExternalScript(false)}
        onError={() => setIsLoadingExternalScript(false)}
      />

      {(mapLoading || isLoadingExternalScript) && (
        <div className={classes.mapLoading}>
          <CircularProgress />
        </div>
      )}

      <div className={classes.searchContainer}>
        <Paper className={classes.searchPaper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Search for any location or address..."
            value={searchValue}
            onChange={handleSearchChange}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            inputProps={{
              'aria-label': 'search location',
              'data-testid': `search-${id}`,
            }}
          />
          {googleCurated.isPending && <CircularProgress size={14} />}
          {searchValue && !googleCurated.isPending && (
            <IconButton size="small" onClick={handleClearSearch}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>

        {showSuggestions && suggestions.length > 0 && (
          <Paper className={classes.autocompleteDropdown}>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={classes.suggestionItem}
                onClick={() => handlePlaceSelect(suggestion)}
              >
                <div style={{ fontWeight: 500, color: '#333', fontSize: 15 }}>
                  {suggestion.displayName}
                </div>
                {suggestion.address && (
                  <div style={{ fontSize: 13, color: '#757575', marginTop: 4 }}>
                    {suggestion.address}
                  </div>
                )}
              </div>
            ))}
          </Paper>
        )}
      </div>

      {currentLocation && (
        <div className={classes.centerMarker}>
          <RoomIcon />
        </div>
      )}

      {!mapLoading && !currentLocation && (
        <div className={classes.noLocationOverlay}>
          <Typography variant="body1">
            {`Ensure your pin location matches your actual business. This affects your visibility on Explore.`}{' '}
          </Typography>
        </div>
      )}

      <div
        ref={mapRef}
        className={clsx(classes.map, {
          blurred: !currentLocation && !mapLoading,
        })}
      />
    </div>
  )
}

Map.propTypes = {
  children: PropTypes.node,
  id: PropTypes.string,
  className: PropTypes.string,
  location: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    lng: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  onLocationChange: PropTypes.func,
}

export default Map
